import {
	ContextActionBar,
	PageContentTitle,
	SearchInputForm,
	SearchPlaceholder
} from "client/Components";
import { DocumentsViewModel, DocumentsViewType } from "client/Api/Documents/SharePoint/common";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { DocumentPageLayout } from "../../DocumentPageLayout";
import { FolderNavigationStackItem } from "../useFolderNavigationStack";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { localize } from "client_legacy/ndaw/culture";
import { EMPTY_ARRAY, isNotNil } from "client/Misc/utils";
import { newGuid } from "client_legacy/ndaw/guid";
import { SharePointRecentlyModified } from "./SharePointRecentlyModified";
import { RecentDocumentActionsWithContext } from "./useRecentDocumentActions";

export type SharePointRecentlyModifiedPageContentProps = {
	isMobile: boolean;
	view: DocumentsViewModel;
	folderPath: readonly FolderNavigationStackItem[];
	layoutKind: DocumentLayoutKind;
	isSharingViaLinkEnabled: boolean;
	onLayoutKindChange(kind: DocumentLayoutKind): void;
};

export const SharePointRecentlyModifiedPageContent = (props: SharePointRecentlyModifiedPageContentProps): ReactElement => {
	const {
		isMobile,
		view,
		folderPath,
		layoutKind,
		isSharingViaLinkEnabled,
		onLayoutKindChange
	} = props;

	if (view.type !== DocumentsViewType.RecentlyModified) {
		throw new Error("Must be a recently modified view");
	}

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({ value: "", version: newGuid() });
	const [actionsWithContext, setActionsWithContext] = useState<RecentDocumentActionsWithContext | null>(null);

	const currentFolder = useMemo(
		(): FolderNavigationStackItem | undefined => {
			return folderPath[folderPath.length - 1];
		},
		[folderPath]
	);

	const actionsChangeHandler = useCallback(
		(value: RecentDocumentActionsWithContext): (() => void) => {
			setActionsWithContext(value);
			return () => setActionsWithContext(null);
		},
		[]
	);

	const shouldShowActionBar = useMemo(
		(): boolean => {
			if (isMobile) {
				return true;
			}

			return isNotNil(actionsWithContext)
				&& actionsWithContext.context
					.selection
					.length > 0;
		},
		[actionsWithContext, isMobile]
	);

	return (
		<DocumentPageLayout trackingId="sharepoint-page-content">
			<DocumentPageLayout.Header>
				{isMobile && (
					<DocumentPageLayout.BreadCrumb>
						<h3>{localize("_RecentlyModified")}</h3>
					</DocumentPageLayout.BreadCrumb>
				)}
				{shouldShowActionBar
					? (
						<DocumentPageLayout.ActionBarContainer>
							<ContextActionBar
								{...(
									actionsWithContext
										? actionsWithContext
										: { actions: EMPTY_ARRAY, context: undefined!}
								)}
								trackingId="global-action-bar"
								horizontalAlignment={isMobile ? "default" : "page-content"}
								hasBorderBottom={isMobile}
							/>
						</DocumentPageLayout.ActionBarContainer>
					)
					: (
						<PageContentTitle
							{...(
								actionsWithContext
									? { actionContext: actionsWithContext.context, actions: actionsWithContext.actions}
									: { actions: undefined, actionContext: undefined }
							)}
							title={folderPath[folderPath.length - 1].name}
						/>
					)
				}
				<DocumentPageLayout.SortAndFilter>
					<SearchInputForm
						value={searchTerm}
						onChange={(value, version) => setSearchTerm({ value, version })}
						placeholder={
							<SearchPlaceholder
								kind="search"
								properties={[localize("_Name")]}
							/>
						}
						height="large"
					/>

					<DocumentPageLayout.LayoutKindChangeButton
						layoutKind={layoutKind}
						onLayoutKindChange={onLayoutKindChange}
					/>
				</DocumentPageLayout.SortAndFilter>
			</DocumentPageLayout.Header>

			<DocumentPageLayout.Content>
				{currentFolder && (
					<SharePointRecentlyModified
						view={view}
						isMobile={isMobile}
						layoutKind={layoutKind}
						searchTerm={searchTerm}
						onActionsChanged={actionsChangeHandler}
						isSharingViaLinkEnabled={isSharingViaLinkEnabled}
					/>
				)}
			</DocumentPageLayout.Content>
		</DocumentPageLayout>
	);
};
SharePointRecentlyModifiedPageContent.displayName = "SharePointRecentlyModifiedPageContent";
